import pluralize from 'pluralize';
import { memo, type Ref } from 'react';

import { WorkspaceAgentPillLeadingVisual } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPillLeadingVisual';
import { WORKSPACE_AGENT_PANEL_UNCITED_PEEK_THUMBNAIL } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { WorkspaceAgentAssetListRows } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetListRows';
import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';
import { WorkspaceAgentDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/disclosure/WorkspaceAgentDisclosure';
import { getWorkspaceAgentUncitedScopeCountLabel } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentUncitedScopeCountLabel';

const MAX_PEEK_THUMBS = 3;

export type WorkspaceAgentUncitedAssetScopeProps = {
  containerRef?: Ref<HTMLDivElement>;
  itemCount: number;
  onExpandedChange?: (isExpanded: boolean) => void;
  onOpenThumbnail?: (id: string) => void;
  thumbnails: readonly WorkspaceAgentThumbnail[];
  unavailableCount: number;
};

export const WorkspaceAgentUncitedAssetScope = memo(
  ({
    containerRef,
    itemCount,
    onExpandedChange,
    onOpenThumbnail,
    thumbnails,
    unavailableCount,
  }: WorkspaceAgentUncitedAssetScopeProps) => {
    if (itemCount === 0) {
      return null;
    }

    const countLabel = getWorkspaceAgentUncitedScopeCountLabel(itemCount, thumbnails);
    const peekThumbs = thumbnails.slice(0, MAX_PEEK_THUMBS);
    const unavailableFooterLabel =
      unavailableCount > 0 ? `${pluralize('asset', thumbnails.length, true)} · ${unavailableCount} unavailable` : null;

    const headerLabel = (
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {peekThumbs.length > 0 ? (
          <span aria-hidden className="flex shrink-0 -space-x-1.5">
            {peekThumbs.map((thumbnail) => (
              <span
                className="relative size-5 shrink-0 overflow-hidden rounded-4 bg-gray-4 ring-1 ring-gray-1"
                key={thumbnail.id}
              >
                <WorkspaceAgentPillLeadingVisual
                  assetType={thumbnail.type ?? null}
                  ext={thumbnail.ext ?? null}
                  thumbnailDimensions={WORKSPACE_AGENT_PANEL_UNCITED_PEEK_THUMBNAIL}
                  thumbnailLayout="fill"
                  thumbnailUrl={thumbnail.thumbnailUrl}
                />
              </span>
            ))}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate font-medium text-12 text-gray-12">{countLabel}</span>
      </span>
    );

    return (
      <div className="w-full min-w-0" ref={containerRef}>
        <WorkspaceAgentDisclosure
          data-testid="WORKSPACE_AGENT_MESSAGE_UNCITED_ASSET_SCOPE"
          onExpandedChange={onExpandedChange}
          trigger={headerLabel}
          triggerTestId="WORKSPACE_AGENT_MESSAGE_UNCITED_ASSET_SCOPE_TRIGGER"
          variant="scope"
        >
          <WorkspaceAgentAssetListRows
            data-testid="WORKSPACE_AGENT_MESSAGE_UNCITED_ASSET_SCOPE_PANEL"
            onOpen={onOpenThumbnail}
            thumbnails={[...thumbnails]}
          />
          {unavailableFooterLabel ? (
            <p className="border-gray-5 border-t px-3 py-1.5 text-12 text-gray-10">{unavailableFooterLabel}</p>
          ) : null}
        </WorkspaceAgentDisclosure>
      </div>
    );
  },
);

WorkspaceAgentUncitedAssetScope.displayName = 'WorkspaceAgentUncitedAssetScope';
